// =============================================================
// User router — intercepts pathname navigation and renders the
// user-facing screens (login, change-password, profile, agenda).
//
// Routes:
//   /login               email + password sign-in
//   /login/change-password  forced new password (after first login)
//   /me                  profile editor
//   /agenda              trainee calendar
//
// Public screens (about, services, locations, contact) fall
// through to the existing site router. So does /agenda when the
// URL is just /agenda without a logged-in user — non-members can
// still see the calendar view.
// =============================================================

import { t, getLocale, applyI18n, onLocaleChange } from './i18n';
import { login, getCachedUser, fetchMe, changePassword, updateProfile, logout } from './userApi';
import type { UserSession } from './content';
import { loginTpl, changePasswordTpl, profileTpl, agendaAppTpl } from './userTemplates';
import { mountAgendaApp } from './agendaApp';
import { sanitizeHtml } from './sanitize';
import { wireReveals } from '../main';
import { getMode, setMode } from './mode';

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, unknown> = {}, ...kids: Array<Node | string | null | undefined | false>): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') node.className = String(v);
    else if (k === 'dataset') Object.assign(node.dataset, v as DOMStringMap);
    else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2), v as EventListener);
    else if (v !== undefined && v !== null) node.setAttribute(k, String(v));
  }
  for (const k of kids) if (k) node.append(k);
  return node;
};

function stripLocale(path: string): string {
  return path.replace(/^\/(?:hu|en)(?=\/|$)/, '');
}

function pushUserRoute(path: string): void {
  const locale = getLocale();
  history.pushState({}, '', `/${locale}${path}`);
}

// Remember the last-rendered user route so we can re-render it on
// locale change (the templates inline `t(...)` strings at render time,
// so flipping locale without re-rendering leaves stale copy on screen).
let lastUserRoute: string | null = null;

/** Render the user shell + the requested subview. */
export async function renderUserRoute(path: string): Promise<boolean> {
  const stripped = stripLocale(path);
  if (
    stripped === '/login' ||
    stripped === '/login/change-password' ||
    stripped === '/me' ||
    stripped === '/agenda'
  ) {
    lastUserRoute = stripped;
    if (stripped === '/login') return await renderLogin();
    if (stripped === '/login/change-password') return await renderChangePassword();
    if (stripped === '/me') return await renderProfile();
    if (stripped === '/agenda') return await renderAgenda();
  }
  return false;
}

async function renderLogin(): Promise<boolean> {
  const app = document.getElementById('app');
  if (!app) return false;
  app.innerHTML = sanitizeHtml(loginTpl());
  applyI18n(app);
  wireReveals();

  const form = document.getElementById('loginForm') as HTMLFormElement | null;
  const errEl = document.getElementById('loginError') as HTMLParagraphElement | null;
  if (!form) return true;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
    const email = (form.querySelector('#loginEmail') as HTMLInputElement).value.trim();
    const password = (form.querySelector('#loginPassword') as HTMLInputElement).value;
    try {
      const user = await login(email, password);
      // Member's discipline is the authoritative site flavor. Snap the
      // local mode to it so /agenda, /me, and the navbar render in the
      // right palette immediately. Anonymous visitors keep their toggle.
      if (user.discipline && user.discipline !== getMode()) {
        setMode(user.discipline);
      }
      // Redirect: change-password if forced, otherwise agenda.
      const next = user.passwordChangeRequired ? '/login/change-password' : '/agenda';
      pushUserRoute(next);
      await renderUserRoute(next);
      // The login handler bypasses main.ts renderRoute(), so the navbar
      // block (rendered by renderHeaderUser) hasn't been told the user is
      // now logged in. Refresh it explicitly.
      renderHeaderUser();
    } catch {
      if (errEl) {
        errEl.hidden = false;
        errEl.textContent = t('login.error');
      }
    }
  });
  return true;
}

async function renderChangePassword(): Promise<boolean> {
  const app = document.getElementById('app');
  if (!app) return false;
  // Best-effort user fetch — if not logged in, redirect to login.
  const user = await fetchMe().catch(() => null);
  if (!user) {
    pushUserRoute('/login');
    return await renderLogin();
  }

  app.innerHTML = sanitizeHtml(changePasswordTpl());
  applyI18n(app);
  wireReveals();

  const form = document.getElementById('changePasswordForm') as HTMLFormElement | null;
  const errEl = document.getElementById('cpError') as HTMLParagraphElement | null;
  if (!form) return true;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
    const current = (form.querySelector('#cpCurrent') as HTMLInputElement).value;
    const next = (form.querySelector('#cpNew') as HTMLInputElement).value;
    const confirm = (form.querySelector('#cpConfirm') as HTMLInputElement).value;
    if (next.length < 8) {
      if (errEl) { errEl.hidden = false; errEl.textContent = t('changePassword.tooShort'); }
      return;
    }
    if (next !== confirm) {
      if (errEl) { errEl.hidden = false; errEl.textContent = t('changePassword.mismatch'); }
      return;
    }
    try {
      await changePassword({
        currentPassword: user.passwordChangeRequired ? undefined : current,
        newPassword: next,
      });
      pushUserRoute('/me');
      await renderProfile();
      // Same rationale as the login handler: bypasses main.ts, so refresh
      // the navbar block ourselves (e.g. passwordChangeRequired may have
      // flipped off, which is reflected via the cached user).
      renderHeaderUser();
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (errEl) {
        errEl.hidden = false;
        errEl.textContent = status === 401 ? t('changePassword.wrongCurrent') : (err as Error).message || t('common.error');
      }
    }
  });
  return true;
}

async function renderProfile(): Promise<boolean> {
  const app = document.getElementById('app');
  if (!app) return false;
  const user = await fetchMe().catch(() => null);
  if (!user) {
    pushUserRoute('/login');
    return await renderLogin();
  }

  app.innerHTML = sanitizeHtml(profileTpl(user.firstName, user.lastName, user.email, user.phone));
  applyI18n(app);
  wireReveals();

  const form = document.getElementById('profileForm') as HTMLFormElement | null;
  const status = document.getElementById('profileStatus') as HTMLParagraphElement | null;
  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (status) { status.classList.remove('is-success', 'is-error'); status.textContent = ''; }
      try {
        const updated = await updateProfile({
          firstName: (form.querySelector('#pfFirst') as HTMLInputElement).value.trim(),
          lastName: (form.querySelector('#pfLast') as HTMLInputElement).value.trim(),
          email: (form.querySelector('#pfEmail') as HTMLInputElement).value.trim(),
          phone: (form.querySelector('#pfPhone') as HTMLInputElement).value.trim(),
        });
        if (status) {
          status.classList.add('is-success');
          status.textContent = t('profile.saved');
        }
        // Update cached user.
        getCachedUser();
        // Refresh header so the name updates.
        updateHeaderUser(updated);
      } catch (err) {
        if (status) {
          status.classList.add('is-error');
          status.textContent = (err as Error).message || t('common.error');
        }
      }
    });
  }

  document.getElementById('goChangePassword')?.addEventListener('click', (e) => {
    e.preventDefault();
    pushUserRoute('/login/change-password');
    void renderChangePassword();
  });

  document.getElementById('profileLogout')?.addEventListener('click', () => {
    // logout() clears the cached user and redirects to /<locale>/login.
    void logout();
  });

  return true;
}

async function renderAgenda(): Promise<boolean> {
  const app = document.getElementById('app');
  if (!app) return false;
  app.innerHTML = sanitizeHtml(agendaAppTpl());
  applyI18n(app);
  wireReveals();
  await mountAgendaApp();
  return true;
}

// ---- header user menu (login / profile / logout) ----
export function renderHeaderUser(): void {
  const menu = document.getElementById('navMenu');
  if (!menu) return;

  // Tear down any previous render so we never double up.
  menu.querySelector('.nav__user')?.remove();

  // Re-query the CTA AFTER removal: renderNav() may have just rebuilt the
  // menu in a sibling subscriber (main.ts's locale listener) and any
  // element we captured earlier could already be detached. Only an
  // element still parented by `menu` is a legal insertBefore reference.
  const cta = Array.from(menu.children).find((n) => n.classList.contains('nav__cta')) ?? null;

  const user = getCachedUser();
  const wrap = el('div', { class: 'nav__user' });
  if (user) {
    // Single full-name anchor that navigates to /me. Logout lives on /me
    // itself rather than here, so the navbar stays calm.
    const fullName = `${user.firstName} ${user.lastName}`.trim();
    const nameLink = el('a', {
      class: 'nav__user-link nav__user-link--name',
      href: `/${getLocale()}/me`,
      'data-nav': '',
      'data-slug': 'me',
      'aria-label': t('nav.profile'),
      title: fullName,
    }, fullName);
    wrap.append(nameLink);
  } else {
    const login = el('a', { class: 'nav__cta nav__cta--login', href: `/${getLocale()}/login`, 'data-nav': '', 'data-slug': 'login' }, t('nav.login'));
    wrap.append(login);
  }

  // Place the wrap right before the CTA. If the captured cta is no longer
  // a child of `menu` (out-of-order subscriber fires), fall back to
  // appending — better a duplicate login than a thrown NotFoundError.
  if (cta && cta.parentNode === menu) {
    menu.insertBefore(wrap, cta);
  } else {
    menu.appendChild(wrap);
  }
}

function updateHeaderUser(user: UserSession): void {
  renderHeaderUser();
  void user;
}

// Hook locale change: re-render the user header (label translations),
// and if the visitor is currently on a user route, re-render that route
// too — the templates inline `t(...)` strings at render time, so flipping
// locale without re-rendering leaves stale copy on screen until the next
// page load. The header re-render is still needed for the navbar block.
onLocaleChange(() => {
  renderHeaderUser();
  if (lastUserRoute) {
    // Re-render through renderUserRoute() (not the inner render* helpers)
    // so lastUserRoute stays accurate for subsequent locale flips.
    void renderUserRoute(lastUserRoute);
  }
});
